// [COPIAR CÓDIGO]
//
// /* GS://PROTOCOLO
//  * Arquivo: C:\Users\Usuario\GoldenSisters_Project\goldensisters_admin_web\src\pages\PurchasesPage.js
//  * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
//  * Versão: 1.8 – Normalização robusta de datas no histórico de compras.
//  * Data: 21/11/2025
//  * Responsável: ChatGPT (conforme Protocolo)
//  *
//  * Mudanças desta versão:
//  * - MANTIDO: Ajustes anteriores de exibição de valores de itens.
//  * - ADICIONADO: Função getPurchaseDate para normalizar datas (Timestamp, Date, objeto seconds ou string).
//  * - AJUSTADO: Ordenação, filtros, impressão e tabela para usarem getPurchaseDate, eliminando o erro purchaseDate.toDate.
//  */
//
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getPurchases } from '../services/firestoreService';
import { Loader2, Printer, Pencil } from 'lucide-react';

const PurchasesPage = ({ openModal }) => {
  const [purchases, setPurchases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterDate, setFilterDate] = useState('');
  const [filterInvoice, setFilterInvoice] = useState('');

  // [GS-CORRECAO-INICIO: Normalização robusta de data da compra]
  const getPurchaseDate = (purchase) => {
    if (!purchase) return null;

    const raw =
      purchase.purchaseDate ??
      purchase.date ??
      purchase.createdAt ??
      null;

    if (!raw) return null;

    // Firestore Timestamp (tem .toDate)
    if (typeof raw?.toDate === 'function') {
      try {
        const d = raw.toDate();
        return isNaN(d.getTime()) ? null : d;
      } catch {
        // segue para as demais tentativas
      }
    }

    // Objeto com seconds (Timestamp bruto)
    if (typeof raw === 'object' && typeof raw.seconds === 'number') {
      const d = new Date(raw.seconds * 1000);
      return isNaN(d.getTime()) ? null : d;
    }

    // Date nativo
    if (raw instanceof Date) {
      return isNaN(raw.getTime()) ? null : raw;
    }

    // String (ISO ou similar)
    if (typeof raw === 'string' || typeof raw === 'number') {
      const d = new Date(raw);
      return isNaN(d.getTime()) ? null : d;
    }

    return null;
  };
  // [GS-CORRECAO-FIM]

  const fetchPurchases = useCallback(async () => {
    setIsLoading(true);
    try {
      const fetchedData = await getPurchases();

      // [GS-CORRECAO-INICIO: Ordenação usando data normalizada]
      fetchedData.sort((a, b) => {
        const dateA = getPurchaseDate(a);
        const dateB = getPurchaseDate(b);
        const timeA = dateA ? dateA.getTime() : 0;
        const timeB = dateB ? dateB.getTime() : 0;
        return timeB - timeA; // mais recente primeiro
      });
      // [GS-CORRECAO-FIM]

      setPurchases(fetchedData);
    } catch (error) {
      console.error('Erro ao carregar histórico de compras:', error);
      openModal(
        <p className="text-red-500">
          Não foi possível carregar o histórico de compras. Verifique o console (F12) para detalhes técnicos.
        </p>,
        'Erro de Carregamento'
      );
    } finally {
      setIsLoading(false);
    }
  }, [openModal]);

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases]);

  const formatCurrency = (value) =>
    (Number(value) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const formatDate = (date) =>
    date ? date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A';

  // Nova função para obter o custo do item de forma segura
  const getUnitCost = (item) => {
    if (item?.unitCostFinal !== undefined && item?.unitCostFinal !== null) {
      return Number(item.unitCostFinal);
    }
    if (item?.unitCost !== undefined && item?.unitCost !== null) {
      return Number(item.unitCost);
    }
    if (item?.unitCostOriginal !== undefined && item?.unitCostOriginal !== null) {
      return Number(item.unitCostOriginal);
    }
    return 0;
  };

  const filteredPurchases = purchases.filter((purchase) => {
    let matches = true;

    if (filterDate) {
      const d = getPurchaseDate(purchase);
      const purchaseDateString = d ? d.toISOString().split('T')[0] : null;
      if (purchaseDateString !== filterDate) matches = false;
    }

    if (filterInvoice) {
      if (
        !purchase.invoiceNumber ||
        !purchase.invoiceNumber.toLowerCase().includes(filterInvoice.toLowerCase())
      ) {
        matches = false;
      }
    }

    return matches;
  });

  const handlePrint = () => {
    if (filteredPurchases.length === 0) return;

    const reportContent = `
      <div style="padding: 20px;">
        <h1 style="font-size: 24px; text-align: center; margin-bottom: 15px; color: black;">Histórico de Compras</h1>
        <p style="font-size: 16px; text-align: center; margin-bottom: 20px; color: black;">
          Data do Relatório: ${formatDate(new Date())}
          ${filterDate ? `<br>Filtrado por Data: ${formatDate(new Date(filterDate))}` : ''}
          ${filterInvoice ? `<br>Filtrado por NF: ${filterInvoice}` : ''}
        </p>

        <div style="margin-top: 20px;">
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="background-color: #f2f2f2;">
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; color: black;">Data</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; color: black;">NF</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; color: black;">Fornecedor</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; color: black;">Itens (Qtde/Custo Unit.)</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: right; font-size: 12px; color: black;">Subtotal</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: right; font-size: 12px; color: black;">Custos Adicionais</th>
                <th style="border: 1px solid #ddd; padding: 8px; text-align: right; font-size: 12px; color: black;">Total da Compra</th>
              </tr>
            </thead>
            <tbody>
              ${filteredPurchases
                .map((purchase) => {
                  const d = getPurchaseDate(purchase);
                  return `
                    <tr style="background-color: white;">
                      <td style="border: 1px solid #ddd; padding: 8px; font-size: 11px; color: black;">${formatDate(
                        d
                      )}</td>
                      <td style="border: 1px solid #ddd; padding: 8px; font-size: 11px; color: black;">${
                        purchase.invoiceNumber || '—'
                      }</td>
                      <td style="border: 1px solid #ddd; padding: 8px; font-size: 11px; color: black;">${
                        purchase.supplierName || 'N/A'
                      }</td>
                      <td style="border: 1px solid #ddd; padding: 8px; font-size: 11px; color: black;">
                        ${
                          purchase.items
                            ?.map(
                              (item) => `
                              <div style="display: flex; justify-content: space-between;">
                                <span>${item.productName} (${item.quantity}x)</span>
                                <span>${formatCurrency(getUnitCost(item))}</span>
                              </div>
                            `
                            )
                            .join('') || ''
                        }
                      </td>
                      <td style="border: 1px solid #ddd; padding: 8px; text-align: right; font-size: 11px; color: black;">${formatCurrency(
                        purchase.subtotal || 0
                      )}</td>
                      <td style="border: 1px solid #ddd; padding: 8px; text-align: right; font-size: 11px; color: black;">${formatCurrency(
                        purchase.totalExtraCosts || 0
                      )}</td>
                      <td style="border: 1px solid #ddd; padding: 8px; text-align: right; font-size: 11px; font-weight: bold; color: black;">${formatCurrency(
                        purchase.totalPurchaseValue || 0
                      )}</td>
                    </tr>
                  `;
                })
                .join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;

    const printWindow = window.open('', '_blank', 'height=800,width=1200');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Histórico de Compras</title>
            <style>
              @page { size: A4 landscape; margin: 20mm; }
              body { font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 10pt; background-color: white; color: black; }
              table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 9pt; }
              th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
              th { background-color: #f2f2f2; color: black; }
              tr:nth-child(even) { background-color: #f9f9f9; }
              .header-print { font-size: 1.6em; text-align: center; margin-bottom: 15px; color: black; }
              .subheader-print { font-size: 1em; text-align: center; margin-bottom: 10px; color: black; }
            </style>
          </head>
          <body>
            ${reportContent}
            <script>window.onload = function(){ window.print(); }</script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-brand-black text-white">
        <Loader2 className="h-12 w-12 animate-spin text-brand-gold" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 bg-brand-black min-h-screen text-white font-sans">
      {/* Barra superior (não imprime) */}
      <div className="no-print-content">
        <h1 className="text-3xl font-bold font-serif mb-6">Histórico de Compras</h1>
        <p className="mb-8 text-gray-300">Visualize todas as compras registradas no sistema.</p>

        <div className="flex justify-end mb-4">
          <button
            onClick={handlePrint}
            disabled={filteredPurchases.length === 0}
            className="flex items-center px-4 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-600 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            <Printer className="mr-2 h-5 w-5" /> Imprimir Histórico
          </button>
        </div>
      </div>

      {/* Cabeçalho para impressão */}
      <div className="print-area">
        <h1 className="header-print">Histórico de Compras</h1>
        <p className="subheader-print">Data do Relatório: {formatDate(new Date())}</p>
        {filterDate && (
          <p className="subheader-print">Filtrado por Data: {formatDate(new Date(filterDate))}</p>
        )}
        {filterInvoice && (
          <p className="subheader-print">Filtrado por NF: {filterInvoice}</p>
        )}

        {/* Filtros (ocultos na impressão) */}
        <div className="bg-brand-dark p-6 rounded-lg mb-6 border border-gray-700 no-print-content">
          <h2 className="text-xl font-semibold mb-4 text-gray-200">Filtros</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="filterDate"
                className="block text-sm font-medium text-gray-400"
              >
                Filtrar por Data
              </label>
              <input
                type="date"
                id="filterDate"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="mt-1 block w-full bg-gray-800 border-gray-600 rounded-md shadow-sm p-2 text-white focus:ring-brand-gold focus:border-brand-gold"
              />
            </div>
            <div>
              <label
                htmlFor="filterInvoice"
                className="block text-sm font-medium text-gray-400"
              >
                Filtrar por Nota Fiscal
              </label>
              <input
                type="text"
                id="filterInvoice"
                value={filterInvoice}
                onChange={(e) => setFilterInvoice(e.target.value)}
                placeholder="Digite o número da NF"
                className="mt-1 block w-full bg-gray-800 border-gray-600 rounded-md shadow-sm p-2 text-white focus:ring-brand-gold focus:border-brand-gold"
              />
            </div>
          </div>
          <button
            onClick={fetchPurchases}
            className="mt-4 px-6 py-2 bg-brand-gold text-brand-black font-semibold rounded-md hover:bg-yellow-500 transition-colors"
          >
            Recarregar Compras
          </button>
        </div>

        {/* Tabela */}
        <div className="bg-brand-dark p-6 rounded-lg mb-6 border border-gray-700">
          <h2 className="text-xl font-semibold mb-4 text-gray-200 no-print-content">
            Lista de Compras
          </h2>
          {filteredPurchases.length === 0 ? (
            <p className="text-gray-500">
              Nenhuma compra encontrada para os filtros aplicados.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      NF
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Fornecedor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Itens (Qtde/Custo Unit.)
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Subtotal
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Custos Adicionais
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Total da Compra
                    </th>
                    {/* Coluna Ações – só na tela */}
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider no-print-content">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-brand-dark divide-y divide-gray-700">
                  {filteredPurchases.map((purchase) => {
                    const d = getPurchaseDate(purchase);
                    return (
                      <tr key={purchase.id} className="hover:bg-gray-800">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {formatDate(d)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {purchase.invoiceNumber || '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {purchase.supplierName || 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-300">
                          {purchase.items?.map((item, idx) => (
                            <div key={idx} className="flex justify-between">
                              <span>
                                {item.productName} ({item.quantity}x)
                              </span>
                              <span>{formatCurrency(getUnitCost(item))}</span>
                            </div>
                          ))}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-yellow-400">
                          {formatCurrency(purchase.subtotal || 0)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-500">
                          {formatCurrency(purchase.totalExtraCosts || 0)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-brand-gold">
                          {formatCurrency(purchase.totalPurchaseValue || 0)}
                        </td>
                        {/* Ações */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right no-print-content">
                          <Link
                            to={`/purchases/${purchase.id}/edit`}
                            className="inline-flex items-center px-3 py-1.5 rounded-md bg-brand-gold text-brand-black hover:bg-yellow-500 transition-colors"
                            title="Editar compra"
                          >
                            <Pencil className="h-4 w-4 mr-1" /> Editar
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchasesPage;
